import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { CompanyMergesTable } from '../-components/CompanyMergesTable';
import { companyMergesListQueryOptions } from '../-hooks/useCompanyMergesList';
import { useCompanyMergesPage } from '../-hooks/useCompanyMergesPage';

const companyMergesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  // The endpoint caps page size at 100.
  limit: fallback(z.number().int().min(10).max(100), 50),
  search: z.string().optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/company-merges/(pages)/')({
  validateSearch: companyMergesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.search !== undefined ? { search: search.search } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed: the page reports a failed load as a banner above the table.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(companyMergesListQueryOptions(deps))
      .catch(() => undefined),
  component: CompanyMergesPage,
});

function CompanyMergesPage() {
  const page = useCompanyMergesPage();

  return (
    <PageContainer>
      <CompanyMergesTable page={page} />
    </PageContainer>
  );
}
